import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PlayerPicker from "../components/PlayerPicker";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { apiClient, API } from "../App";
import SortableTableHead from "../components/SortableTableHead";
import useTableControls from "../hooks/useTableControls";
import useScrollRestoration from "../hooks/useScrollRestoration";
import { ArrowLeft, PoundSterling, Plus, Pencil, Trash2, Download, TrendingUp, TrendingDown, BarChart3 } from "lucide-react";

const fmt = (n) => `£${Number(n || 0).toFixed(2)}`;

// Iter-69: column config for sort + per-column filter dropdowns on the
// Transactions table.
const TX_COLUMNS = [
  { key: "date", label: "Date", accessor: (r) => r.date ? new Date(r.date) : null, sortable: true, filterable: false },
  { key: "type", label: "Type", accessor: "type", sortable: true, filterable: true },
  { key: "category", label: "Category", accessor: "category_name", sortable: true, filterable: true },
  { key: "description", label: "Description", accessor: "description", sortable: true, filterable: false },
  { key: "amount", label: "Amount", accessor: (r) => Number(r.amount || 0), sortable: true, filterable: false },
  { key: "paid", label: "Paid", accessor: (r) => !!r.paid, sortable: true, filterable: true, filterFormatter: (v) => v ? "Paid" : "Unpaid" },
  { key: "auto_generated", label: "Auto", accessor: (r) => !!r.auto_generated, sortable: true, filterable: true, filterFormatter: (v) => v ? "Auto-posted" : "Manual" },
];

const VARIANCE_COLUMNS = [
  { key: "category_name", label: "Category", accessor: "category_name", sortable: true, filterable: false },
  { key: "type", label: "Type", accessor: "type", sortable: true, filterable: true },
  { key: "budget", label: "Budget", accessor: (r) => r.budget_annual != null ? Number(r.budget_annual) : null, sortable: true, filterable: false },
  { key: "actual", label: "Actual", accessor: (r) => Number(r.actual || 0), sortable: true, filterable: false },
  { key: "variance", label: "Variance", accessor: (r) => r.variance != null ? Number(r.variance) : null, sortable: true, filterable: false },
  { key: "pct", label: "%", accessor: (r) => r.variance_pct != null ? Number(r.variance_pct) : null, sortable: true, filterable: false },
];

const ExportMenu = ({ onPdf, onExcel, testid }) => (
  <div className="flex gap-2">
    <Button size="sm" variant="outline" onClick={onPdf} data-testid={`${testid}-pdf`}>
      <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
    </Button>
    <Button size="sm" variant="outline" onClick={onExcel} data-testid={`${testid}-excel`}>
      <Download className="w-3.5 h-3.5 mr-1.5" /> Excel
    </Button>
  </div>
);

const Finance = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  // iter-86: preserve scroll position on detail navigation
  useScrollRestoration();
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [variance, setVariance] = useState(null);
  const [yoy, setYoy] = useState(null);
  const [events, setEvents] = useState([]);
  const [players, setPlayers] = useState([]);
  const [sections, setSections] = useState([]);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [catDialog, setCatDialog] = useState({ open: false, data: null });
  const [txDialog, setTxDialog] = useState({ open: false, data: null });

  // iter-149e: per-FY Budget tab state
  const [budgetSettings, setBudgetSettings] = useState(null);
  const [budgetYear, setBudgetYear] = useState(null);
  const [budget, setBudget] = useState(null);
  const [budgetDrafts, setBudgetDrafts] = useState({}); // category_id -> string
  const [budgetSaving, setBudgetSaving] = useState(false);

  // Iter-69: sort + per-column filter state for the two main tables.
  const txCtl = useTableControls(transactions, TX_COLUMNS);
  const varianceCtl = useTableControls(variance?.rows || [], VARIANCE_COLUMNS);

  const loadAll = async () => {
    try {
      const [cats, tx, sum, ev, pl, sx] = await Promise.all([
        apiClient.get("/finance/categories"),
        apiClient.get("/finance/transactions", {
          params: { from_date: fromDate || undefined, to_date: toDate || undefined },
        }),
        apiClient.get("/finance/reports/summary", {
          params: { from_date: fromDate || undefined, to_date: toDate || undefined },
        }),
        apiClient.get("/events"),
        apiClient.get("/players"),
        apiClient.get("/sections").catch(() => ({ data: [] })),
      ]);
      setCategories(cats.data);
      setTransactions(tx.data);
      setSummary(sum.data);
      setEvents(ev.data || []);
      setPlayers(pl.data || []);
      setSections(sx.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load finance data");
    }
  };

  const loadVariance = async () => {
    try {
      const res = await apiClient.get("/finance/reports/variance");
      setVariance(res.data);
    } catch (e) { console.error(e); }
  };

  const loadYoy = async () => {
    try {
      const res = await apiClient.get("/finance/reports/yoy");
      setYoy(res.data);
    } catch (e) { console.error(e); }
  };

  // iter-149e — Budget tab
  const loadBudgetSettings = async () => {
    try {
      const res = await apiClient.get("/finance/budget/settings");
      setBudgetSettings(res.data);
      if (budgetYear == null) setBudgetYear(res.data.current_fy_year);
    } catch (e) { console.error(e); }
  };

  const loadBudget = async (yr) => {
    if (yr == null) return;
    try {
      const res = await apiClient.get(`/finance/budget?year=${yr}`);
      setBudget(res.data);
      const drafts = {};
      (res.data.rows || []).forEach((r) => { drafts[r.category_id] = String(r.amount || ""); });
      setBudgetDrafts(drafts);
    } catch (e) { console.error(e); toast.error("Failed to load budget"); }
  };

  const handleSaveBudget = async () => {
    if (budgetYear == null) return;
    setBudgetSaving(true);
    try {
      const payload = {
        budgets: Object.entries(budgetDrafts).map(([category_id, v]) => ({
          category_id,
          amount: parseFloat(v || 0) || 0,
        })),
      };
      await apiClient.put(`/finance/budget?year=${budgetYear}`, payload);
      toast.success(`FY${String(budgetYear).padStart(2,"0")} budget saved`);
      await loadBudget(budgetYear);
      await loadBudgetSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save budget");
    } finally {
      setBudgetSaving(false);
    }
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line
  useEffect(() => {
    if (tab === "variance") loadVariance();
    if (tab === "yoy") loadYoy();
    if (tab === "budget") { loadBudgetSettings(); }
  }, [tab]);
  useEffect(() => {
    if (tab === "budget" && budgetYear != null) loadBudget(budgetYear);
  }, [tab, budgetYear]); // eslint-disable-line

  const handleSaveCategory = async () => {
    const d = catDialog.data;
    if (!d?.name || !d?.type) { toast.error("Name & type required"); return; }
    try {
      if (d.id) {
        await apiClient.put(`/finance/categories/${d.id}`, {
          name: d.name,
          color: d.color,
        });
        toast.success("Category updated");
      } else {
        await apiClient.post("/finance/categories", {
          type: d.type,
          name: d.name,
          color: d.color || "#E6B800",
        });
        toast.success("Category created");
      }
      setCatDialog({ open: false, data: null });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await apiClient.delete(`/finance/categories/${id}`);
      toast.success("Deleted");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleSaveTx = async () => {
    const d = txDialog.data;
    if (!d?.category_id || !d?.amount || !d?.date || !d?.description) {
      toast.error("All fields required"); return;
    }
    const cat = categories.find((c) => c.id === d.category_id);
    try {
      if (d.id) {
        await apiClient.put(`/finance/transactions/${d.id}`, {
          category_id: d.category_id,
          amount: parseFloat(d.amount),
          date: d.date,
          description: d.description,
          event_id: d.event_id || null,
          player_id: d.player_id || null,
          paid: d.paid,
          paid_date: d.paid_date || null,
          notes: d.notes || null,
        });
        toast.success("Transaction updated");
      } else {
        await apiClient.post("/finance/transactions", {
          type: cat.type,
          category_id: d.category_id,
          amount: parseFloat(d.amount),
          date: d.date,
          description: d.description,
          event_id: d.event_id || null,
          player_id: d.player_id || null,
          paid: d.paid ?? true,
          paid_date: d.paid_date || null,
          notes: d.notes || null,
        });
        toast.success("Transaction added");
      }
      setTxDialog({ open: false, data: null });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleDeleteTx = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    try {
      await apiClient.delete(`/finance/transactions/${id}`);
      toast.success("Deleted");
      loadAll();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const exportFile = async (url, filename) => {
    try {
      const res = await apiClient.get(url, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="finance-page">
      <Button variant="ghost" onClick={() => navigate("/admin")} data-testid="finance-back">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
      </Button>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <PoundSterling className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-4xl tracking-tight">Finance</h1>
          <p className="text-muted-foreground mt-1">Income, expenditure, categories, budgets & reports.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="overflow-x-auto -mx-2 sm:mx-0 px-2 sm:px-0">
          <TabsList data-testid="finance-tabs" className="w-max">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
            <TabsTrigger value="budget" data-testid="tab-budget">Budget</TabsTrigger>
            <TabsTrigger value="variance" data-testid="tab-variance">Budget vs Actual</TabsTrigger>
            <TabsTrigger value="yoy" data-testid="tab-yoy">Year-over-Year</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="dashboard" className="space-y-4 mt-4">
          <div className="flex gap-3 items-end flex-wrap">
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="filter-from" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="filter-to" />
            </div>
            <Button onClick={loadAll} data-testid="filter-apply">Apply</Button>
            <div className="ml-auto">
              <ExportMenu
                testid="finance-summary-export"
                onPdf={() => exportFile(
                  `/reports/finance/summary.xlsx?${new URLSearchParams({ ...(fromDate && {from_date: fromDate}), ...(toDate && {to_date: toDate}) })}`.replace('.xlsx', '.pdf'),
                  `finance-summary.pdf`
                )}
                onExcel={() => exportFile(
                  `/reports/finance/summary.xlsx?${new URLSearchParams({ ...(fromDate && {from_date: fromDate}), ...(toDate && {to_date: toDate}) })}`,
                  `finance-summary.xlsx`
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="glass-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
                  <TrendingUp className="w-4 h-4 text-emerald-500" /> Income
                </div>
                <div className="text-3xl font-heading mt-2 text-emerald-500" data-testid="stat-income">
                  {fmt(summary?.total_income)}
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
                  <TrendingDown className="w-4 h-4 text-rose-500" /> Expense
                </div>
                <div className="text-3xl font-heading mt-2 text-rose-500" data-testid="stat-expense">
                  {fmt(summary?.total_expense)}
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
                  <BarChart3 className="w-4 h-4 text-primary" /> Net
                </div>
                <div className={`text-3xl font-heading mt-2 ${(summary?.net || 0) >= 0 ? "text-emerald-500" : "text-rose-500"}`} data-testid="stat-net">
                  {fmt(summary?.net)}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="glass-card">
              <CardHeader><CardTitle>Income by Category</CardTitle></CardHeader>
              <CardContent>
                {(summary?.income_by_category || []).length === 0 && <p className="text-sm text-muted-foreground">No income recorded.</p>}
                {(summary?.income_by_category || []).map((c) => (
                  <div key={c.name} className="flex justify-between py-1.5 border-b border-white/5 last:border-0">
                    <span>{c.name}</span><span className="font-mono">{fmt(c.amount)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardHeader><CardTitle>Expense by Category</CardTitle></CardHeader>
              <CardContent>
                {(summary?.expense_by_category || []).length === 0 && <p className="text-sm text-muted-foreground">No expenditure recorded.</p>}
                {(summary?.expense_by_category || []).map((c) => (
                  <div key={c.name} className="flex justify-between py-1.5 border-b border-white/5 last:border-0">
                    <span>{c.name}</span><span className="font-mono">{fmt(c.amount)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="transactions" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">{transactions.length} transaction(s)</p>
            <div className="flex gap-2">
              <ExportMenu
                testid="tx-export"
                onPdf={() => exportFile(`/reports/finance/transactions.xlsx`.replace('.xlsx','.pdf'), `transactions.pdf`)}
                onExcel={() => exportFile(`/reports/finance/transactions.xlsx`, `transactions.xlsx`)}
              />
              <Button onClick={() => setTxDialog({ open: true, data: { type: "income", paid: true, date: new Date().toISOString().slice(0,10) } })} data-testid="add-tx-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Transaction
              </Button>
            </div>
          </div>
          <Card className="glass-card">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {TX_COLUMNS.map((col) => (
                      <SortableTableHead
                        key={col.key}
                        column={col}
                        sortKey={txCtl.sortKey}
                        sortDir={txCtl.sortDir}
                        onToggleSort={() => txCtl.toggleSort(col.key)}
                        rows={transactions}
                        selectedFilters={txCtl.filters[col.key]}
                        onChangeFilter={(set) => txCtl.setColumnFilter(col.key, set)}
                        className={col.key === "amount" ? "text-right" : undefined}
                      />
                    ))}
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {txCtl.processedRows.map((t) => (
                    <TableRow key={t.id} data-testid={`tx-row-${t.id}`}>
                      <TableCell>{t.date}</TableCell>
                      <TableCell>
                        <Badge variant={t.type === "income" ? "default" : "secondary"}>{t.type}</Badge>
                      </TableCell>
                      <TableCell>{t.category_name}</TableCell>
                      <TableCell>{t.description}</TableCell>
                      <TableCell className={`text-right font-mono ${t.type === "income" ? "text-emerald-500" : "text-rose-500"}`}>{fmt(t.amount)}</TableCell>
                      <TableCell>{t.paid ? "✓" : "—"}</TableCell>
                      <TableCell>{t.auto_generated ? <Badge variant="outline" className="text-xs">auto</Badge> : ""}</TableCell>
                      <TableCell className="text-right">
                        <Button size="icon" variant="ghost" onClick={() => setTxDialog({ open: true, data: { ...t } })} data-testid={`edit-tx-${t.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDeleteTx(t.id)} data-testid={`delete-tx-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {txCtl.processedRows.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">{transactions.length === 0 ? "No transactions yet." : "No transactions match your filters."}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="space-y-4 mt-4">
          <div className="flex justify-between">
            <p className="text-sm text-muted-foreground">Configure income/expenditure categories. Budgets are managed on the Budget tab.</p>
            <Button onClick={() => setCatDialog({ open: true, data: { type: "income" } })} data-testid="add-cat-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Category
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["income", "expenditure"].map((t) => (
              <Card key={t} className="glass-card">
                <CardHeader><CardTitle className="capitalize">{t}</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {categories.filter((c) => c.type === t).map((c) => (
                    <div key={c.id} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0" data-testid={`cat-row-${c.id}`}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ background: c.color || "#E6B800" }} />
                        <span>{c.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="icon" variant="ghost" onClick={() => setCatDialog({ open: true, data: { ...c } })}><Pencil className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDeleteCategory(c.id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="budget" className="space-y-4 mt-4" data-testid="budget-tab-content">
          {(() => {
            const s = budgetSettings;
            if (!s) return <p className="text-sm text-muted-foreground">Loading budget settings…</p>;
            const fyLabel = (yr) => `FY${String(yr).padStart(2, "0")}`;
            const incomeRows = (budget?.rows || []).filter((r) => r.type === "income");
            const expRows = (budget?.rows || []).filter((r) => r.type === "expenditure");
            const totals = (rows) => rows.reduce((a, r) => ({
              amount: a.amount + Number(r.amount || 0),
              spent: a.spent + Number(r.spent || 0),
            }), { amount: 0, spent: 0 });
            const incT = totals(incomeRows);
            const expT = totals(expRows);
            const isArchived = budgetYear != null && !s.available_years.includes(budgetYear);
            return (
              <>
                <div className="flex flex-wrap items-end gap-3 justify-between">
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <Label className="text-xs">Financial year</Label>
                      <Select
                        value={budgetYear != null ? String(budgetYear) : ""}
                        onValueChange={(v) => setBudgetYear(parseInt(v, 10))}
                      >
                        <SelectTrigger className="w-[160px]" data-testid="budget-year-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {s.available_years.map((y) => (
                            <SelectItem key={y} value={String(y)}>{fyLabel(y)}{y === s.current_fy_year ? " (current)" : ""}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {s.archived_years.length > 0 && (
                      <div>
                        <Label className="text-xs">Previous years</Label>
                        <Select
                          value={isArchived ? String(budgetYear) : ""}
                          onValueChange={(v) => setBudgetYear(parseInt(v, 10))}
                        >
                          <SelectTrigger className="w-[180px]" data-testid="budget-archive-select">
                            <SelectValue placeholder="Open archived FY…" />
                          </SelectTrigger>
                          <SelectContent>
                            {s.archived_years.map((y) => (
                              <SelectItem key={y} value={String(y)}>{fyLabel(y)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {budget && (
                      <p className="text-xs text-muted-foreground pb-2">
                        {budget.from_date} → {budget.to_date}
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={handleSaveBudget}
                    disabled={budgetSaving || !budget}
                    data-testid="save-budget-btn"
                  >
                    {budgetSaving ? "Saving…" : "Save Budget"}
                  </Button>
                </div>

                {["income", "expenditure"].map((section) => {
                  const rows = section === "income" ? incomeRows : expRows;
                  const t = section === "income" ? incT : expT;
                  return (
                    <Card key={section} className="glass-card">
                      <CardHeader><CardTitle className="capitalize">{section}</CardTitle></CardHeader>
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Category</TableHead>
                                <TableHead className="text-right w-[180px]">Budget (£)</TableHead>
                                <TableHead className="text-right">{section === "income" ? "Received" : "Spent"} (£)</TableHead>
                                <TableHead className="text-right">Remaining (£)</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {rows.length === 0 && (
                                <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-4">No {section} categories</TableCell></TableRow>
                              )}
                              {rows.map((r) => {
                                const draft = budgetDrafts[r.category_id] ?? "";
                                const draftNum = parseFloat(draft || 0) || 0;
                                const remaining = draftNum - Number(r.spent || 0);
                                return (
                                  <TableRow key={r.category_id} data-testid={`budget-row-${r.category_id}`}>
                                    <TableCell>{r.category_name}</TableCell>
                                    <TableCell className="text-right">
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={draft}
                                        onChange={(e) => setBudgetDrafts({ ...budgetDrafts, [r.category_id]: e.target.value })}
                                        className="text-right font-mono h-8"
                                        data-testid={`budget-input-${r.category_id}`}
                                      />
                                    </TableCell>
                                    <TableCell className="text-right font-mono">{fmt(r.spent)}</TableCell>
                                    <TableCell className={`text-right font-mono ${remaining >= 0 ? "text-emerald-500" : "text-rose-500"}`}>{fmt(remaining)}</TableCell>
                                  </TableRow>
                                );
                              })}
                              {rows.length > 0 && (
                                <TableRow className="border-t border-white/10 font-medium">
                                  <TableCell>Total</TableCell>
                                  <TableCell className="text-right font-mono">{fmt(t.amount)}</TableCell>
                                  <TableCell className="text-right font-mono">{fmt(t.spent)}</TableCell>
                                  <TableCell className={`text-right font-mono ${(t.amount - t.spent) >= 0 ? "text-emerald-500" : "text-rose-500"}`}>{fmt(t.amount - t.spent)}</TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </>
            );
          })()}
        </TabsContent>

        <TabsContent value="variance" className="space-y-4 mt-4">
          <p className="text-sm text-muted-foreground">Budget vs Actual for {variance?.year || "current year"}.</p>
          <Card className="glass-card">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  {VARIANCE_COLUMNS.map((col) => (
                    <SortableTableHead
                      key={col.key}
                      column={col}
                      sortKey={varianceCtl.sortKey}
                      sortDir={varianceCtl.sortDir}
                      onToggleSort={() => varianceCtl.toggleSort(col.key)}
                      rows={variance?.rows || []}
                      selectedFilters={varianceCtl.filters[col.key]}
                      onChangeFilter={(set) => varianceCtl.setColumnFilter(col.key, set)}
                      className={["budget","actual","variance","pct"].includes(col.key) ? "text-right" : undefined}
                    />
                  ))}
                </TableRow></TableHeader>
                <TableBody>
                  {varianceCtl.processedRows.map((r) => (
                    <TableRow key={r.category_id}>
                      <TableCell>{r.category_name}</TableCell>
                      <TableCell><Badge variant="outline">{r.type}</Badge></TableCell>
                      <TableCell className="text-right font-mono">{r.budget_annual != null ? fmt(r.budget_annual) : "—"}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(r.actual)}</TableCell>
                      <TableCell className={`text-right font-mono ${(r.variance || 0) >= 0 ? "text-emerald-500" : "text-rose-500"}`}>{r.variance != null ? fmt(r.variance) : "—"}</TableCell>
                      <TableCell className="text-right">{r.variance_pct != null ? `${r.variance_pct}%` : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="yoy" className="space-y-4 mt-4">
          <p className="text-sm text-muted-foreground">Year-over-year comparison.</p>
          <Card className="glass-card">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Year</TableHead>
                  <TableHead className="text-right">Income</TableHead>
                  <TableHead className="text-right">Expense</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead className="text-right">#TX</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(yoy?.years || []).map((y) => (
                    <TableRow key={y.year}>
                      <TableCell>{y.year}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-500">{fmt(y.income)}</TableCell>
                      <TableCell className="text-right font-mono text-rose-500">{fmt(y.expense)}</TableCell>
                      <TableCell className={`text-right font-mono ${y.net >= 0 ? "text-emerald-500" : "text-rose-500"}`}>{fmt(y.net)}</TableCell>
                      <TableCell className="text-right">{y.transaction_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Category Dialog */}
      <Dialog open={catDialog.open} onOpenChange={(o) => !o && setCatDialog({ open: false, data: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{catDialog.data?.id ? "Edit" : "Add"} Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={catDialog.data?.type} onValueChange={(v) => setCatDialog({ open: true, data: { ...catDialog.data, type: v } })} disabled={!!catDialog.data?.id}>
                <SelectTrigger data-testid="cat-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expenditure">Expenditure</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name</Label>
              <Input value={catDialog.data?.name || ""} onChange={(e) => setCatDialog({ open: true, data: { ...catDialog.data, name: e.target.value } })} data-testid="cat-name" />
            </div>
            <div>
              <Label>Colour</Label>
              <Input type="color" value={catDialog.data?.color || "#E6B800"} onChange={(e) => setCatDialog({ open: true, data: { ...catDialog.data, color: e.target.value } })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialog({ open: false, data: null })}>Cancel</Button>
            <Button onClick={handleSaveCategory} data-testid="cat-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transaction Dialog */}
      <Dialog open={txDialog.open} onOpenChange={(o) => !o && setTxDialog({ open: false, data: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{txDialog.data?.id ? "Edit" : "Add"} Transaction</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select
                  value={txDialog.data?.type || "income"}
                  onValueChange={(v) => setTxDialog({ open: true, data: { ...txDialog.data, type: v, category_id: "" } })}
                  disabled={!!txDialog.data?.id}
                >
                  <SelectTrigger data-testid="tx-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expenditure">Expenditure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={txDialog.data?.category_id || ""}
                  onValueChange={(v) => setTxDialog({ open: true, data: { ...txDialog.data, category_id: v } })}
                >
                  <SelectTrigger data-testid="tx-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {categories.filter((c) => c.type === (txDialog.data?.type || "income")).map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount (£)</Label>
                <Input type="number" step="0.01" value={txDialog.data?.amount ?? ""} onChange={(e) => setTxDialog({ open: true, data: { ...txDialog.data, amount: e.target.value } })} data-testid="tx-amount" />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={txDialog.data?.date || ""} onChange={(e) => setTxDialog({ open: true, data: { ...txDialog.data, date: e.target.value } })} data-testid="tx-date" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={txDialog.data?.description || ""} onChange={(e) => setTxDialog({ open: true, data: { ...txDialog.data, description: e.target.value } })} data-testid="tx-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Link to event (optional)</Label>
                <Select value={txDialog.data?.event_id || "none"} onValueChange={(v) => setTxDialog({ open: true, data: { ...txDialog.data, event_id: v === "none" ? null : v } })}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {events.slice(0, 100).map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Link to player (optional)</Label>
                <PlayerPicker
                  value={txDialog.data?.player_id || null}
                  players={players}
                  sections={sections}
                  onChange={(v) => setTxDialog({ open: true, data: { ...txDialog.data, player_id: v } })}
                  placeholder="None"
                  allowClear
                  testId="finance-tx-player"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={txDialog.data?.paid ?? true} onCheckedChange={(v) => setTxDialog({ open: true, data: { ...txDialog.data, paid: !!v } })} id="tx-paid" />
              <Label htmlFor="tx-paid">Paid</Label>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={txDialog.data?.notes || ""} onChange={(e) => setTxDialog({ open: true, data: { ...txDialog.data, notes: e.target.value } })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTxDialog({ open: false, data: null })}>Cancel</Button>
            <Button onClick={handleSaveTx} data-testid="tx-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Finance;
